export interface NetworkInfo {
  name: string;
  fee: number;
  time: string;
}

export interface CryptoAsset {
  symbol: string;
  name: string;
  nameAr: string;
  price: number;
  sellPrice: number;
  change24h: number;
  icon: string;
  image?: string;
  color: string;
  networks: NetworkInfo[];
  market_cap_rank?: number;
}

const PROFIT_MARGIN = 1.20;

// Polygon (POS) is the only supported network. Gas is very cheap (paid in POL).
const POLYGON_NETWORK: NetworkInfo = { name: "Polygon (POS)", fee: 0.5, time: "1-3 دقيقة" };

export const KNOWN_CRYPTO_META: Record<string, {
  icon: string; nameAr: string; nameEn: string; color: string; networks: NetworkInfo[]
}> = {
  POL:     { icon: "⬢", nameAr: "بولي (MATIC)", nameEn: "Polygon", color: "hsl(var(--crypto-purple))", networks: [POLYGON_NETWORK] },
  WETH:    { icon: "Ξ", nameAr: "إيثيريوم ملفوف", nameEn: "Wrapped Ether", color: "hsl(var(--crypto-purple))", networks: [POLYGON_NETWORK] },
  WBTC:    { icon: "₿", nameAr: "بيتكوين ملفوف", nameEn: "Wrapped Bitcoin", color: "hsl(var(--crypto-gold))", networks: [POLYGON_NETWORK] },
  USDT:    { icon: "₮", nameAr: "تيذر", nameEn: "Tether USD", color: "hsl(var(--crypto-green))", networks: [POLYGON_NETWORK] },
  USDC:    { icon: "$", nameAr: "يو إس دي سي", nameEn: "USD Coin (Bridged)", color: "hsl(var(--crypto-blue))", networks: [POLYGON_NETWORK] },
  DAI:     { icon: "◈", nameAr: "داي", nameEn: "Dai", color: "hsl(var(--crypto-gold))", networks: [POLYGON_NETWORK] },
  STMATIC: { icon: "⬢", nameAr: "ستاكد ماتيك", nameEn: "Staked MATIC", color: "hsl(var(--crypto-purple))", networks: [POLYGON_NETWORK] },
  WSTETH:  { icon: "Ξ", nameAr: "wstETH", nameEn: "Wrapped stETH", color: "hsl(var(--crypto-purple))", networks: [POLYGON_NETWORK] },
  AAVE:    { icon: "👻", nameAr: "آفي", nameEn: "Aave", color: "hsl(var(--crypto-purple))", networks: [POLYGON_NETWORK] },
  LINK:    { icon: "⬡", nameAr: "تشينلينك", nameEn: "Chainlink", color: "hsl(var(--crypto-blue))", networks: [POLYGON_NETWORK] },
  UNI:     { icon: "🦄", nameAr: "يونيسواب", nameEn: "Uniswap", color: "hsl(var(--crypto-red))", networks: [POLYGON_NETWORK] },
  CRV:     { icon: "◠", nameAr: "كيرف", nameEn: "Curve DAO", color: "hsl(var(--crypto-red))", networks: [POLYGON_NETWORK] },
  SUSHI:   { icon: "🍣", nameAr: "سوشي", nameEn: "SushiSwap", color: "hsl(var(--crypto-red))", networks: [POLYGON_NETWORK] },
  MKR:     { icon: "♦", nameAr: "ميكر", nameEn: "Maker", color: "hsl(var(--crypto-green))", networks: [POLYGON_NETWORK] },
  COMP:    { icon: "◉", nameAr: "كومباوند", nameEn: "Compound", color: "hsl(var(--crypto-green))", networks: [POLYGON_NETWORK] },
  BAL:     { icon: "⊕", nameAr: "بالانسر", nameEn: "Balancer", color: "hsl(var(--crypto-blue))", networks: [POLYGON_NETWORK] },
  SNX:     { icon: "◆", nameAr: "سينثيتيكس", nameEn: "Synthetix", color: "hsl(var(--crypto-blue))", networks: [POLYGON_NETWORK] },
  GRT:     { icon: "◈", nameAr: "ذا غراف", nameEn: "The Graph", color: "hsl(var(--crypto-purple))", networks: [POLYGON_NETWORK] },
  "1INCH": { icon: "🦄", nameAr: "1inch", nameEn: "1inch", color: "hsl(var(--crypto-red))", networks: [POLYGON_NETWORK] },
  LDO:     { icon: "🔷", nameAr: "ليدو", nameEn: "Lido DAO", color: "hsl(var(--crypto-blue))", networks: [POLYGON_NETWORK] },
};

const DEFAULT_NETWORKS: NetworkInfo[] = [POLYGON_NETWORK];

const COLOR_PALETTE = [
  "hsl(var(--crypto-blue))",
  "hsl(var(--crypto-purple))",
  "hsl(var(--crypto-green))",
  "hsl(var(--crypto-gold))",
  "hsl(var(--crypto-red))",
];

export function buildCryptoAsset(data: {
  symbol: string;
  name: string;
  price: number;
  sellPrice: number;
  change24h: number;
  image?: string;
  market_cap_rank?: number;
}): CryptoAsset {
  const meta = KNOWN_CRYPTO_META[data.symbol];
  const colorIndex = data.symbol.charCodeAt(0) % COLOR_PALETTE.length;

  return {
    symbol: data.symbol,
    name: data.name,
    nameAr: meta?.nameAr || data.name,
    price: data.price,
    sellPrice: data.sellPrice,
    change24h: data.change24h,
    icon: meta?.icon || data.symbol.charAt(0),
    image: data.image,
    color: meta?.color || COLOR_PALETTE[colorIndex],
    networks: meta?.networks || DEFAULT_NETWORKS,
    market_cap_rank: data.market_cap_rank,
  };
}

export const cryptoAssets: CryptoAsset[] = [
  buildCryptoAsset({ symbol: "POL", name: "Polygon", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "WETH", name: "Wrapped Ether", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "WBTC", name: "Wrapped Bitcoin", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "USDT", name: "Tether USD", price: 1.0, sellPrice: 1.0 * PROFIT_MARGIN, change24h: 0 }),
  buildCryptoAsset({ symbol: "USDC", name: "USD Coin (Bridged)", price: 1.0, sellPrice: 1.0 * PROFIT_MARGIN, change24h: 0 }),
  buildCryptoAsset({ symbol: "DAI", name: "Dai", price: 1.0, sellPrice: 1.0 * PROFIT_MARGIN, change24h: 0 }),
  buildCryptoAsset({ symbol: "STMATIC", name: "Staked MATIC", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "WSTETH", name: "Wrapped stETH", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "AAVE", name: "Aave", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "LINK", name: "Chainlink", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "UNI", name: "Uniswap", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "CRV", name: "Curve DAO", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "SUSHI", name: "SushiSwap", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "MKR", name: "Maker", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "COMP", name: "Compound", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "BAL", name: "Balancer", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "SNX", name: "Synthetix", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "GRT", name: "The Graph", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "1INCH", name: "1inch", price: 0, sellPrice: 0, change24h: 0 }),
  buildCryptoAsset({ symbol: "LDO", name: "Lido DAO", price: 0, sellPrice: 0, change24h: 0 }),
];

export const formatCurrency = (amount: number, currency: string = "USD"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatCrypto = (amount: number): string => {
  return amount.toFixed(8).replace(/\.?0+$/, "");
};
